<template>
<div class="view-wrap">
	<Heading :level="1" text="Your Application Dashboard" />
	<Heading v-if="getCompanyName" :level="2" class="minr-h4" :text="`Welcome, ${getCompanyName}`" />

	<p class="m-b-2">Below you will find links to start or continue any of your pending applications. Once you have completed your application you will be able to check back for it's current status.</p>
	<p class="m-b-2">The large capital grant fund is currently on hold if you have any questions please email - <a href="mailto:durhambusinessgrowth@weareumi.co.uk" target="_blank" rel="nofollow">Durhambusinessgrowth@weareumi.co.uk</a>.</p>

	<UserMessage v-if="eoiError && eoiError.status" :message-type="eoiError.type" :message-text="eoiError.message" />
	<UserMessage v-if="appError.small && appError.small.status" :message-type="appError.small.type" :message-text="appError.small.message" />
	<UserMessage v-if="appError.large && appError.large.status" :message-type="appError.large.type" :message-text="appError.large.message" />
	<UserMessage v-if="claimError && claimError.status" :message-type="claimError.type" :message-text="claimError.message" />

	<div>
		<ul class="applicationList">
			<li v-if="getEOIId">
				<ApplicationListItem heading="Your Expression of Interest" :id="getEOIId" :status="eoiStatusCode" :status-text="standardiseStatus(getEOIStatus)" :steps="stageOneSteps" application-type="eoi" />
			</li>
		</ul>
		<ul class="applicationList">
			<li v-if="getApplicationId('small')">
				<ApplicationListItem heading="Your Application - Small Grant" :id="getApplicationId('small')" :status="appStatusCode.small" :status-text="standardiseStatus(getApplicationStatus('small'))" :steps="stageTwoStepsSmall" application-type="application" application-size="small" />
			</li>
			<!-- Only show application if stage two isn't closed OR if they've already submitted an app  -->
			<li v-if="getClaimsDownloaded && getAppSubmitted('small')">
				<Heading :level="2" text="Your Claims - Small Grant" />
				<ul v-if="getClaims('small')?.length">
					<li v-for="(claim, ind) in getClaims('small')" :key="ind">
						<ApplicationListItem :heading="`Claim Created: ${$filters.userFriendlyDate(claim.createdon)}`" :id="claim.ua_icclaimid" :status="claimStatus(claim)" application-type="claim" application-size="small" />
					</li>
				</ul>
				<p v-else class="m-t-2">
					No pending claims were found for this application
				</p>
			</li>
		</ul>
		<ul class="applicationList">
			<!-- Only show application if stage two isn't closed OR if they've already submitted an app  -->
			<li v-if="getClaimsDownloaded && getAppSubmitted('large')">
				<Heading :level="2" text="Your Claims - Large Grant" />
				<ul v-if="getClaims('large')?.length">
					<li v-for="(claim, ind) in getClaims('large')" :key="ind">
						<ApplicationListItem :heading="`Claim Created: ${$filters.userFriendlyDate(claim.createdon)}`" :id="claim.ua_icclaimid" :status="claimStatus(claim)" application-type="claim" application-size="large" />
					</li>
				</ul>
				<p v-else class="m-t-2">
					No pending claims were found for this application
				</p>
			</li>
		</ul>
	</div>
</div>
</template>

	<script>
import { mapGetters, mapActions } from 'vuex';

import ApplicationListItem from '@/components/ApplicationListItem';
import CookieJar from '@/utilities/cookieJar';
import { STATUS_MAP } from '@/utilities/constants';

export default {
	name: 'DashboardView',
	components: {
		ApplicationListItem
	},
	inject: ['stageOneSteps', 'stageTwoStepsLarge', 'stageTwoStepsSmall'],
	layout: 'auth',
	data () {
		return {
			eoiStatusCode: 0,
			appStatusCode: {
				small: 0,
				large: 0
			},
			claimStatusCode: 0,
			hcStatusCode: 0,
			eoiError: {
				status: null,
				type: '',
				message: ''
			},
			appError: {
				small: {
					status: null,
					type: '',
					message: ''
				},
				large: {
					status: null,
					type: '',
					message: ''
				}
			},
			claimError: {
				status: null,
				type: '',
				message: ''
			},
			eoiDocsOpen: false,
			appDocsOpen: {
				small: false,
				large: false
			},
			claimsDocsOpen: {
				small: false,
				large: false
			}
		};
	},
	head: {
		title: `Your dashboard | ${process.env.VUE_APP_FUND_NAME}`,
		meta: [
			{ name: 'description', content: `Welcome to the ${process.env.VUE_APP_FUND_NAME} dashboard` }
		]
	},
	computed: {
			...mapGetters('eoi', ['getEOISubmitted', 'getEOIId', 'getEOIStatus', 'getApplicationTypes', 'getReadyForFunding', 'getCompanyName']),
			...mapGetters('application', ['getAppSubmitted', 'getApplicationId', 'getApplicationStatus']),
			...mapGetters('claim', ['getClaims']),
			...mapGetters('settings', ['getClaimsDownloaded'])
	},
	mounted () {
		this.eoiStatus();
		this.getExistingEOI();
	},
	methods: {
		checkLoginStatus (callback) {
			this.redeemToken({
				refreshtoken: CookieJar.get(`${process.env.VUE_APP_COOKIE_PREFIX}refresh_token`),
				businessunit: process.env.VUE_APP_BUSINESS_UNIT
			}).then((res) => {
				if (res === true) {
					this[callback]();
					return true;
				}
				this.$router.push({ name: 'Login' });
			}).catch(() => {
				this.$router.push({ name: 'Login' });
			});
		},
		getExistingEOI () {
			if (!this.getEOIId) {
				this.getEOI().then((res) => {
					if (res === true) {
						this.eoiStatus();
						const { small, large } = this.getApplicationTypes;
						if (small) {
							this.getExistingApplication('small');
						}
						if (large) {
							this.getExistingApplication('large');
						}
					} else if (res.status === 404) {
						this.createNewEOI();
					} else if (res.status === 401) {
						this.checkLoginStatus('getExistingEOI');
					} else {
						this.eoiError = res;
					}
				}).catch((err) => {
					this.eoiError = err;
				});
			} else {
				if (this.getReadyForFunding) {
					const { small, large } = this.getApplicationTypes;
					if (small) {
						this.getExistingApplication('small');
					}
					if (large) {
						this.getExistingApplication('large');
					}
				}
			}
		},
		createNewEOI () {
			this.createEOI().then((res) => {
				if (res === true) {
					this.eoiStatus();
					if (this.getReadyForFunding) {
						const { small, large } = this.getApplicationTypes;
						if (small) {
							this.getExistingApplication('small');
						}
						if (large) {
							this.getExistingApplication('large');
						}
					}
				} else if (res.status === 401) {
					this.checkLoginStatus('createNewEOI');
				} else {
					this.eoiError = res;
				}
			}).catch((err) => {
				this.eoiError = err;
			});
		},
		getExistingApplication (size) {
			if (!this.getApplicationId(size)) {
				this.getApplication().then((res) => {
					if (res) {
						this.applicationStatus(size);
						this.loadClaims(size);
					} else if (res.status === 401) {
						this.checkLoginStatus('getExistingApplication');
					} else {
						this.appError[size] = res;
					}
				}).catch((err) => {
					this.appError = err;
				});
			} else {
				this.applicationStatus(size);
			}
		},
		loadClaims (size) {
			if (!this.getClaimsDownloaded) {
				this.fetchClaims(size).then((res) => {
					if (res.status === 404) {
						this.claimError = res;
					} else if (res.status === 401) {
						this.checkLoginStatus('loadClaims');
					} else {
						this.claimError = res;
					}
				}).catch((err) => {
					this.claimError = err;
				});
			}
		},
		standardiseStatus (status) {
			const statusToCheck = status || 'created';
			return statusToCheck.replace(/[-()]/g, '').replace(/\s+/g, '-').toLowerCase();
		},
		eoiStatus () {
			if (this.getEOIStatus && STATUS_MAP.eoi[this.standardiseStatus(this.getEOIStatus)]) {
				this.eoiStatusCode = STATUS_MAP.eoi[this.standardiseStatus(this.getEOIStatus)];
			} else if (this.getEOISubmitted === 1) {
				this.eoiStatusCode = 1;
			} else {
				this.eoiStatusCode = 0;
			}
		},
		applicationStatus (size) {
			const appStatus = this.getApplicationStatus(size);
			const statusMap = STATUS_MAP.application[this.standardiseStatus(appStatus)];
			if (appStatus && statusMap) {
				this.appStatusCode[size] = statusMap;
			} else if (this.getAppSubmitted(size)) {
				this.appStatusCode[size] = 1;
			} else {
				this.appStatusCode[size] = 0;
			}
		},
		claimStatus (claim) {
			const claimStatus = claim.ua_currentstatus;
			if (claimStatus && STATUS_MAP.claim[this.standardiseStatus(claimStatus)]) {
				return STATUS_MAP.claim[this.standardiseStatus(claimStatus)];
			}

			return claim.ua_currentstatus === 1 ? 1 : 0;
		},
		...mapActions('auth', ['redeemToken']),
		...mapActions('eoi', ['getEOI', 'createEOI']),
		...mapActions('application', ['getApplication', 'prefillApplication']),
		...mapActions('claim', ['fetchClaims'])
	}
};
	</script>

	<style lang="scss" scoped>
	@import '../assets/scss/components/dashboard.scss';
	</style>
